import React from 'react';
import {
  IonButton,
  IonCard,
  IonCardContent,
  IonCardHeader,
  IonCardTitle,
  IonIcon,
  IonInput,
  IonItem,
  IonLabel,
  IonSpinner,
  IonText,
} from '@ionic/react';
import { keyOutline, logInOutline, personOutline } from 'ionicons/icons';

interface SignInFormProps {
  username: string;
  password: string;
  isLoading: boolean;
  signInMessage: string;
  isAppMode: boolean;
  onUsernameChange: (value: string) => void;
  onPasswordChange: (value: string) => void;
  onSubmit: () => void;
  onForgotPassword: () => void;
  onDisconnect: () => void;
}

const SignInForm: React.FC<SignInFormProps> = ({
  username,
  password,
  isLoading,
  signInMessage,
  isAppMode,
  onUsernameChange,
  onPasswordChange,
  onSubmit,
  onForgotPassword,
  onDisconnect,
}) => {
  const loginCardStyle: React.CSSProperties = {
    width: '100%',
    maxWidth: '400px',
    boxShadow: '0 10px 30px rgba(0, 0, 0, 0.2)',
    borderRadius: '16px',
  };

  const loginItemStyle: React.CSSProperties = {
    marginBottom: '1rem',
  };

  const loginButtonStyle: React.CSSProperties = {
    marginTop: '1.5rem',
    height: '48px',
    fontWeight: 600,
  };

  return (
    <>
      {signInMessage && (
        <IonText color="medium">
          <p style={{ textAlign: 'center', margin: '0 0 1rem', lineHeight: 1.5 }}>{signInMessage}</p>
        </IonText>
      )}

      <IonCard style={loginCardStyle}>
        <IonCardHeader>
          <IonCardTitle>Sign In</IonCardTitle>
        </IonCardHeader>
        <IonCardContent>
          <IonItem style={loginItemStyle}>
            <IonIcon icon={personOutline} slot="start" />
            <IonLabel position="stacked" style={{ marginBottom: '5px' }}>Username</IonLabel>
            <IonInput
              value={username}
              placeholder="Enter your User Name"
              onIonInput={(e) => onUsernameChange(e.detail.value ?? '')}
              onKeyUp={(e) => {
                if (e.key === 'Enter') onSubmit();
              }}
              disabled={isLoading}
              clearInput
            />
          </IonItem>

          <IonItem style={loginItemStyle}>
            <IonIcon icon={keyOutline} slot="start" />
            <IonLabel position="stacked" style={{ marginBottom: '5px' }}>Password</IonLabel>
            <IonInput
              type="password"
              value={password}
              placeholder="Enter your Password"
              onIonInput={(e) => onPasswordChange(e.detail.value ?? '')}
              onKeyUp={(e) => {
                if (e.key === 'Enter') onSubmit();
              }}
              disabled={isLoading}
              clearInput
            />
          </IonItem>

          <IonButton
            expand="full"
            onClick={onSubmit}
            disabled={isLoading || !username.trim() || !password.trim()}
            style={loginButtonStyle}
          >
            {isLoading ? (
              <>
                <IonSpinner name="crescent" />
                <span style={{ marginLeft: '8px' }}>Signing in...</span>
              </>
            ) : (
              <>
                <IonIcon icon={logInOutline} slot="start" />
                Sign In
              </>
            )}
          </IonButton>

          <div style={{ display: 'flex', justifyContent: 'center', marginTop: '1rem' }}>
            <IonButton fill="clear" size="small" onClick={onForgotPassword} disabled={isLoading}>
              Forgot Password / Username?
            </IonButton>
          </div>

          {isAppMode && (
            <IonButton fill="outline" expand="full" color="medium" onClick={onDisconnect} style={{ marginTop: '0.75rem' }}>
              Disconnect
            </IonButton>
          )}
        </IonCardContent>
      </IonCard>
    </>
  );
};

export default SignInForm;
